import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Table, Badge, Empty, Select, Button, SortControl, sortRows } from "@/lib/ui";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ClicksHistory() {
  const [rows, setRows] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [sellerId, setSellerId] = useState("");
  const [sort, setSort] = useState({ field: "product", dir: "asc" });
  const SORT_FIELDS = [
    { key: "created_at", label: "Connected On", type: "date" },
    { key: "product", label: "Product", type: "str" },
    { key: "company_name", label: "Company", type: "str" },
    { key: "city", label: "City", type: "str" },
    { key: "buyer_phone", label: "Buyer Mobile", type: "str" },
  ];

  useEffect(() => { api.get("/admin/sellers").then(({ data }) => setSellers(data)).catch(() => setSellers([])); }, []);

  const load = useCallback(() => {
    const params = {};
    if (sellerId) params.seller_id = sellerId;
    api.get("/admin/clicks", { params }).then(({ data }) => setRows(data));
  }, [sellerId]);
  useEffect(() => { load(); }, [load]);

  const sellerName = sellerId ? (sellers.find((s) => s.id === sellerId)?.company_name || sellers.find((s) => s.id === sellerId)?.phone || "Selected seller") : "All Sellers";

  const exportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(15); doc.setTextColor(15, 23, 42);
    doc.text("Deadstock.live - Clicks History", 14, 16);
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text(`Seller: ${sellerName}    Generated: ${new Date().toLocaleString()}    Records: ${rows.length}`, 14, 22);
    autoTable(doc, {
      startY: 27,
      head: [["Buyer Mobile", "Product", "Company (Seller)", "City", "Connected On"]],
      body: rows.map((r) => [
        r.buyer_phone ? `+91 ${r.buyer_phone}` : "-",
        r.product || "-", r.company_name || "-", r.city || "-",
        new Date(r.created_at).toLocaleString(),
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [234, 88, 12] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`clicks-history-${sellerId ? sellerName.replace(/\s+/g, "-") : "all"}-${Date.now()}.pdf`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold text-slate-900">Clicks History</h1>
          <p className="mt-1 text-sm text-slate-500">Proof of who connected with which seller — every WhatsApp-seller click is logged with the buyer's mobile.</p>
        </div>
        <Button variant="dark" onClick={exportPdf} disabled={rows.length === 0} data-testid="clicks-export-pdf"><i className="fa-solid fa-file-pdf" /> Export PDF</Button>
      </div>

      <div className="mt-4 mb-4 flex flex-wrap items-center gap-2">
        <Select className="w-auto min-w-[220px]" value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="clicks-seller-filter">
          <option value="">All Sellers</option>
          {sellers.map((s) => <option key={s.id} value={s.id}>{s.company_name || s.phone} ({s.seller_type})</option>)}
        </Select>
        {sellerId && <Button variant="outline" onClick={() => setSellerId("")}><i className="fa-solid fa-rotate-left" /> Reset</Button>}
        <SortControl fields={SORT_FIELDS} value={sort} onChange={setSort} testid="clicks-sort" />
        <span className="ml-auto text-sm text-slate-500"><b className="text-slate-900">{rows.length}</b> connections</span>
      </div>

      {rows.length === 0 ? <div className="mt-6"><Empty text="No clicks recorded yet" icon="fa-hand-pointer" /></div> : (
        <div className="mt-2">
          <Table head={["Buyer Mobile", "Product", "Company (Seller)", "City", "Connected On"]}>
            {sortRows(rows, SORT_FIELDS, sort).map((r) => (
              <tr key={r.id} data-testid={`click-${r.id}`}>
                <td className="px-4 py-3">
                  {r.buyer_phone ? (
                    <a href={`https://wa.me/91${r.buyer_phone}`} target="_blank" rel="noreferrer"
                      data-testid={`click-wa-${r.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
                      <i className="fa-brands fa-whatsapp" /> +91 {r.buyer_phone}
                    </a>
                  ) : <span className="text-xs text-slate-400"><i className="fa-brands fa-whatsapp mr-1" />Not captured</span>}
                </td>
                <td className="px-4 py-3 font-semibold text-slate-800">{r.product}</td>
                <td className="px-4 py-3">{r.company_name}</td>
                <td className="px-4 py-3">{r.city || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </div>
  );
}
